import { useMemo } from "react";
import { TextFieldElement } from "react-hook-form-mui";
import {
  Alert,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { Box } from "@mui/system";

import {
  getAgeGroups,
  getDemographicsSummary,
  shouldTrackParticipants
} from "@components/Pages/Events/eventFunctions";
import RadioButtonGroup from "@components/common/HookFormMui/RadioButtonGroup";
import { demographicSources } from "@lib/constants";
import {
  preventNonNumericKeyPress,
  validatePositiveInteger
} from "@lib/utils";
import CompleteEventFields from '@components/ContentComponents/Events/CompleteEvent/CompleteEventFields';
import useDemographicOptionsQuery from "@components/common/hooks/api/useDemographicOptionsQuery";
import useSessionParticipantsQuery from "@components/common/hooks/api/useSessionParticipantsQuery";

const DataHeading = styled(Typography)({
  fontSize: '16px',
  lineHeight: '40px',
});

const TableHeadCell = styled(TableCell)({
  backgroundColor: '#f4f4f4',
  fontWeight: 'bold',
  padding: '4px 16px',
  textAlign: 'center',
});

const TableDataCell = styled(TableCell)({
  verticalAlign: 'top',
  textAlign: 'center',
});

const NumberField = styled(TextFieldElement)({
  alignItems: 'center',  // aligns input within wrapper (the border is on the wrapper)
  ".MuiInputBase-root": {
    maxWidth: '100px',
  },
  input: {
    // We add extra padding to the left side of the input element, to move the value closer to the center. Otherwise,
    // the built-in up-down arrows of input type="number" make it look like it's skewed to the left.
    padding: '12px 10px 12px 24px',
    maxWidth: '80px',
    textAlign: 'center',  // aligns the value inside the field
  },
  // Helper text and also error text
  ".MuiFormHelperText-root": {
    maxWidth: '100px',
  }
});

/**
 * Form fields for the Event Demographics form. This does not include a <FormContainer> or any submit logic,
 * so it can be used in a form by itself, or as part of another, larger form.
 */
const EventDemographicsFormFields = ({form, event, showSummary = false, showCompleteConfirmation = false}) => {
  const { data: demographicOptions, ...demographicOptionsQuery } = useDemographicOptionsQuery();
  const ageRanges = getAgeGroups(event, demographicOptions);

  let ageRangeRows = [];
  if (event.curriculum?.audience === 'Family') {
    // Family: split the age range fields onto two rows. "No answer" and "unknown" go in the Adult row.
    ageRangeRows = [
      ageRanges.filter(r => r.audience === 'YOUTH'),
      ageRanges.filter(r => r.audience !== 'YOUTH')
    ];
  } else {
    // Adults/Youth: show all fields in one row.
    ageRangeRows.push(ageRanges);
  }

  // Determine whether participants were entered individually. If they were, the demographic counts are
  // calculated automatically when the participant list is finalized, and we disable some fields on this form.
  const { data: participants } = useSessionParticipantsQuery(event.id);
  const hasIndividualParticipants = shouldTrackParticipants(event) && participants?.length > 0;

  const {
    watch
  } = form;

  const valuesWatch = watch();

  // Update the 'sums must match' messages when values change. These are separate from the regular form field
  // validation, and appear even if the form hasn't been submitted yet.
  const demographicsMessages = useMemo(() => {
    if (!showSummary) {
      return null;
    }
    const summary = getDemographicsSummary(event, valuesWatch);
    return {
      age: !summary.groupsValid.age ? `The sum of age group categories must equal ${summary.participants}. The sum is
          currently ${summary.sums.age}.` : '',
      gender: !summary.groupsValid.gender ? `The sum of gender categories must equal ${summary.participants}. The sum is
          currently ${summary.sums.gender}.` : '',
      race: !summary.groupsValid.race ? `The sum of race and ethnicity categories must be equal to or greater
          than ${summary.participants}. The sum is currently ${summary.sums.race}.` : '',
    };
  }, [event, showSummary, valuesWatch]);

  if (demographicOptionsQuery.isLoading) {
    return null;
  }

  return (
    <Box mt="28px">
      <DataHeading variant="h4">
        AGE GROUP
      </DataHeading>
      {demographicsMessages?.age && (
        <Alert severity="error" sx={{mb: 2}}>
          {demographicsMessages.age}
        </Alert>
      )}

      {ageRangeRows.map((ranges, idx) => (
        <Table key={idx} sx={{
          width: 'auto'
        }} data-cy="table-age">
          <TableHead>
            <TableRow>
              {ranges.map(ageRange => (
                <TableHeadCell key={`th-${ageRange.code}`}>
                  <label className={'required'} htmlFor={`age.${ageRange.code}`}>{ageRange.label}</label>
                </TableHeadCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {ranges.map(ageRange => (
                <TableDataCell key={`td-${ageRange.code}`}>
                  <NumberField
                    id={`field-age-${ageRange.code}`}
                    name={`age.${ageRange.code}`}
                    label=""
                    type="number"
                    control={form.control}
                    required
                    disabled={hasIndividualParticipants}
                    validation={validatePositiveInteger}
                    onKeyDown={preventNonNumericKeyPress}
                    data-cy="field-age"
                  />
                </TableDataCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      ))}

      <DataHeading variant="h4">
        GENDER
      </DataHeading>
      {demographicsMessages?.gender && (
        <Alert severity="error" sx={{mb: 2}}>
          {demographicsMessages.gender}
        </Alert>
      )}
      <Table sx={{
        width: 'auto'
      }} data-cy="table-gender">
        <TableHead>
          <TableRow>
            {demographicOptions.gender.map(gender => (
              <TableHeadCell key={`th-${gender.code}`}>
                <label className={'required'} htmlFor={`gender.${gender.code}`}>{gender.label}</label>
              </TableHeadCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {demographicOptions.gender.map((gender) => (
              <TableDataCell key={`td-${gender.code}`}>
                <NumberField
                  id={`field-gender-${gender.code}`}
                  name={`gender.${gender.code}`}
                  label=""
                  type="number"
                  control={form.control}
                  required
                  disabled={hasIndividualParticipants}
                  validation={validatePositiveInteger}
                  onKeyDown={preventNonNumericKeyPress}
                  data-cy="field-gender"
                />
              </TableDataCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>

      <DataHeading variant="h4">
        RACE AND ETHNICITY
      </DataHeading>
      {demographicsMessages?.race && (
        <Alert severity="error" sx={{mb: 2}}>
          {demographicsMessages.race}
        </Alert>
      )}
      <Table sx={{
        width: 'auto'
      }} data-cy="table-race">
        <TableHead>
          <TableRow>
            {demographicOptions?.raceEthnicity?.map(race => (
              <TableHeadCell sx={{
                maxWidth: 175,
                padding: '4px 8px',
              }} key={`th-${race.code}`}>
                <label className={'required'} htmlFor={`race.${race.code}`}>{race.label}</label>
              </TableHeadCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {demographicOptions?.raceEthnicity?.map(race => (
              <TableDataCell key={`td-${race.code}`}>
                <NumberField
                  id={`field-race-${race.code}`}
                  name={`race.${race.code}`}
                  label=""
                  type="number"
                  control={form.control}
                  required
                  disabled={hasIndividualParticipants}
                  validation={validatePositiveInteger}
                  onKeyDown={preventNonNumericKeyPress}
                  data-cy="field-race"
                />
              </TableDataCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>

      <Box mt="28px">
        <RadioButtonGroup
          id="field-source"
          name="source"
          label={'Who did you get the demographics from?'}
          control={form.control}
          fullWidth
          required
          disabled={hasIndividualParticipants}
          options={demographicSources}
        />
      </Box>

      {showCompleteConfirmation && (
        <Box mt="28px">
          <CompleteEventFields form={form} />
        </Box>
      )}
    </Box>
  );
}
export default EventDemographicsFormFields;
